import BellSlashIcon from '@ar/assets/images/bell-slash-icon.svg';
import SendIcon from '@ar/assets/images/send-icon.svg';
import ArrowRightIcon from '@icons/arrow-right-icon.svg';
import CheckIcon from '@icons/check-icon.svg';
import { Banner, Button, EBannerType, MultiSelectDropdown, toast, Typography } from '@notch-ordering/ui-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getGpoId } from '@v2/utils/GPOUtils';
import { mergeClassNames } from '@v2/utils/Helpers';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { RadioButton } from '@/components/shared';
import { useSupplierStore } from '@/ar/stores/SupplierStore';
import { AccountRole, attachAdyenPaymentMethod, attestMandate, PaymentMethodType, sendMandateEmail } from '@/ar/network/AccountsReceivable.network';
import { FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY, useGetCustomer } from '@/ar/hooks/queries/CustomerQueries.hook';
import { FETCH_BALANCES_QUERY_KEY } from '@/ar/hooks/queries/BalanceQueries.hook';
import { FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY } from '@/ar/components/Invoices/Modals/BulkManualChargeModal/BulkManualChargeModalConstants';
import { MandateTypes } from '../../CreateAdyenPaymentMethodConstants';
import { EncryptedBankData } from '../CreateBankAccountConstants';
import { useEmailOptions } from '@/ar/hooks/useEmailOptions';
import { ConditionalModalWrapper } from '@/ar/components/shared/modals/ConditionalModalWrapper/ConditionalModalWrapper';

export interface MandateModalProps {
    handleClose: () => void,
    bankData?: EncryptedBankData,
    customerID: string,
    supplierID: string,
    resend?: boolean,
    paymentMethodId?: string,
    paymentMethodType?: PaymentMethodType,
    className?: string,
    isOpen: boolean,
    isModal?: boolean,
    title?: React.ReactNode,
}

type AttachPaymentMethodError = { error: string };

export const emailPattern = /^((?!\.)[\w\-_.+]*[^.])(@\w+)(\.\w+(\.\w+)?[^.\W])$/;
export const emailPatternSource = emailPattern.source;

export const MandateModal: React.FC<MandateModalProps> = ({ handleClose, bankData, customerID, supplierID, paymentMethodType, resend, paymentMethodId, className, isOpen, isModal = false, title }) => {
    const { supplierLoginData, supplier } = useSupplierStore();
    const supplierIDToUse = supplierID ?? supplierLoginData?.supplier_id;
    const queryClient = useQueryClient();
    const sendMandateEmailMutation = useMutation(sendMandateEmail);
    const attestMandateMutation = useMutation(attestMandate);
    const attachPaymentMethodMutation = useMutation(attachAdyenPaymentMethod);
    const [hasError, setHasError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selected, setSelected] = useState<MandateTypes>(null);
    const isOfflineMandateEnabled = !!supplier?.is_offline_mandate_enabled;
    const gpoId = getGpoId(supplierLoginData);
    const { data: customer } = useGetCustomer({
        customerID,
        supplierID,
        gpoId
    });

    const { allOptions, defaultOptions, selectedEmails, setSelectedEmails } = useEmailOptions(customer);

    const resetState = () => {
        setHasError(false);
        setSelected(null);
        setErrorMessage('');
    };
    const onClose = () => {
        resetState();
        handleClose();
    };

    const onSubmit = async (): Promise<void> => {
        if (!resend) {
            attachPaymentMethodMutation.mutate({
                body: {
                    bankData: {
                        type: paymentMethodType,
                        accountRole: AccountRole.CHECKING,
                        ownerName: bankData?.encryptedOwnerName,
                        routingNumber: paymentMethodType === PaymentMethodType.US_BANK_ACCOUNT ? bankData?.encryptedRoutingNumber : undefined,
                        transitNumber: paymentMethodType === PaymentMethodType.ACSS_DEBIT ? bankData?.encryptedBranchNumber : undefined,
                        institutionNumber: paymentMethodType === PaymentMethodType.ACSS_DEBIT ? bankData?.encryptedInstitutionNumber : undefined,
                        accountNumber: bankData?.encryptedAccountNumber
                    },
                    customerId: customerID,
                    supplierId: supplierIDToUse,
                    isMandateAttestedOffline: selected === 'offline',
                    emailsForSendingMandate: selectedEmails.length > 0 ? selectedEmails : undefined
                }
            }, {
                onSuccess: async () => {
                    toast.show({
                        message: 'Bank account added successfully',
                    });
                    queryClient.invalidateQueries([FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
                    queryClient.invalidateQueries([FETCH_BALANCES_QUERY_KEY]);
                    queryClient.invalidateQueries([FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
                    onClose();
                },
                onError: (error: AxiosError<AttachPaymentMethodError>) => {
                    setHasError(true);
                    const message = error?.response?.data?.error;
                    if (message.includes('Code')) {
                        setErrorMessage(message);
                    }
                }
            });
        } else if (selected !== 'offline') {
            sendMandateEmailMutation.mutate({
                paymentMethodID: paymentMethodId,
                customerID,
                supplierID,
                emailsForSendingMandate:
                            selectedEmails
            }, {
                onSuccess: () => {
                    toast.show({
                        message: 'Debit agreement sent',
                        icon: <CheckIcon/>,
                    });
                    queryClient.invalidateQueries([FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
                    queryClient.invalidateQueries([FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
                    onClose();
                },
                onError: (response) => {
                    const { data } = (response as AxiosError).response;
                    const { error } = data as { error: string };
                    toast.show({
                        message: `Error sending mandate email: ${error}`,
                    });
                },
            });
        } else {
            attestMandateMutation.mutate({
                paymentMethodID: paymentMethodId,
                customerID,
                supplierID,
                emailsForSendingMandate: selectedEmails.length > 0 ? selectedEmails : undefined
            }, {
                onSuccess: () => {
                    toast.show({
                        message: 'Debit agreement attested',
                        icon: <CheckIcon/>,
                    });
                    queryClient.invalidateQueries([FETCH_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
                    queryClient.invalidateQueries([FETCH_BULK_CUSTOMERS_PAYMENT_METHODS_QUERY_KEY]);
                    onClose();
                },
                onError: (response) => {
                    const { data } = (response as AxiosError).response;
                    const { error } = data as { error: string };
                    toast.show({
                        message: `Error attested mandate: ${error}`,
                    });
                },
            });
        }
    };

    return <ConditionalModalWrapper
        title={title}
        titleSeparatorDesktop={true}
        isModal={isModal}
        isOpen={isOpen}
        close={onClose}
        className={mergeClassNames('', className)}>
        <div className="flex flex-col gap-2 px-8 py-6">
            {!!hasError
                && <Banner alertType={EBannerType.ERROR}
                    className="px-2 rounded-none mb-2"
                    title={<Typography as="div" className="text-red-500 text-2">
                        Unable to add payment method. Please contact Notch.
                    </Typography>}
                    body={<Typography as="div" className="text-gray-600 text-1">
                        {errorMessage}
                    </Typography>} />}
            <Typography variant="LG-1" className="text-gray-600 mb-0">You need your customer&apos;s authorization before pulling a bank payment, which can be done in person, writing, electronically, or by phone.</Typography>
            <div className="flex flex-col gap-2 items-center relative">
                <Button
                    className={mergeClassNames('text-left flex py-4 text-gray-700 w-full', selected === 'email' && 'border-gray-700')}
                    variant="TERTIARY_OUTLINED"
                    size="MEDIUM"
                    id="email-button"
                    onClick={() => setSelected(MandateTypes.EMAIL)}>
                    <SendIcon className={mergeClassNames('h-5 w-5 text-gray-600 absolute right-4', selected === 'email' && 'text-gray-700')} />
                    <RadioButton id="email"
                        name="email"
                        label=""
                        isChecked={selected === MandateTypes.EMAIL}
                        onChecked={() => setSelected(MandateTypes.EMAIL)}/>
                    <div className="flex-row gap-3 items-center">
                        <Typography variant="LG-1" className="text-gray-700 mb-0">Don’t have authorization yet? Send your customer a pre-authorized debit agreement</Typography>
                        <Typography variant="BASE" className="text-gray-600 mt-1 mb-2">Your customer will receive a simple one-click authorization agreement. You can begin charging them as soon as the agreement is accepted. </Typography>
                        <MultiSelectDropdown
                            defaultValues={defaultOptions}
                            editPattern={emailPatternSource}
                            onChange={(choseOptions) => setSelectedEmails(choseOptions.map((option) => option.value))}
                            notCorrectNewValueMessage="(not correct value yet)"
                            options={allOptions}/>
                    </div>
                </Button>
                {isOfflineMandateEnabled && <Button
                    id="offline-button"
                    className={mergeClassNames('text-left flex py-4 text-gray-700 w-full relative', selected === 'offline' && 'border-gray-700')}
                    variant="TERTIARY_OUTLINED"
                    size="MEDIUM"
                    onClick={() => setSelected(MandateTypes.OFFLINE)}>
                    <BellSlashIcon className={mergeClassNames('h-5 w-5 text-gray-600 absolute right-4', selected === 'offline' && 'text-gray-700')} />
                    <RadioButton id="offline"
                        name="offline"
                        label=""
                        isChecked={selected === MandateTypes.OFFLINE}
                        onChecked={() => setSelected(MandateTypes.OFFLINE)}/>
                    <div className="flex-row gap-3">
                        <Typography variant="LG-1" className="text-gray-700 mb-0 pr-6">Already have verbal or written authorization from your customer? </Typography>
                        <Typography variant="BASE" className="text-gray-600 mt-1 mb-2">We’ll send your customer a confirmation and you start charging right away.</Typography>
                        <MultiSelectDropdown
                            defaultValues={defaultOptions}
                            editPattern={emailPatternSource}
                            onChange={(choseOptions) => setSelectedEmails(choseOptions.map((option) => option.value))}
                            notCorrectNewValueMessage="(not correct value yet)"
                            options={allOptions}/>
                    </div>
                </Button>}
            </div>
        </div>
        <div className="w-full h-px bg-gray-200" />
        <div className="pt-4 mx-6 flex justify-self-end">
            <Button
                className="mr-3 cursor-pointer"
                type="button"
                as="div"
                onClick={onClose}
                variant="TERTIARY_FILLED"
                size="MEDIUM"
                minWidth="w-auto">
                Cancel
            </Button>
            <Button
                disabled={selected === null}
                variant="SECONDARY"
                size="MEDIUM"
                minWidth="w-auto"
                type="submit"
                className="flex items-center"
                loading={attachPaymentMethodMutation.isLoading}
                onClick={onSubmit}>
                {selected === MandateTypes.OFFLINE ? 'Save' : 'Save & Request Signature' }<ArrowRightIcon className="h-5 w-5 ml-1"/>
            </Button>
        </div>
    </ConditionalModalWrapper>;
};
